import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Link } from "react-router-dom";

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const FieldLabel = ({ children }) => (
  <div style={{
    color: "#6b7280", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8,
  }}>
    {children}
  </div>
);

const PasswordInput = ({ value, onChange, placeholder, show, autoComplete }) => (
  <div style={{ position: "relative" }}>
    <input
      type={show ? "text" : "password"}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      style={{ ...inputStyle, paddingRight: 44 }}
      {...focusHandlers}
    />
  </div>
);

const RequirementRow = ({ met, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: met ? "#16a34a" : "#9ca3af" }}>
    <i className={`fas ${met ? "fa-check-circle" : "fa-circle"}`} style={{ fontSize: 11, color: met ? "#16a34a" : "#d1d5db" }} />
    {label}
  </div>
);

const ChangePasswordModal = ({ domain, show, handleClose, userData }) => {
  const [step, setStep] = useState(0);
  const userId = userData?.[0];

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (!show) {
      setStep(0);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswords(false);
      setIsSubmitting(false);
      setErrors([]);
      setServerError("");
    }
  }, [show]);

  const tryParseJSON = (str) => {
    try { return JSON.parse(str); } catch { return null; }
  };

  // Live requirement checks
  const reqLength   = newPassword.length >= 6;
  const reqLower    = /[a-z]/.test(newPassword);
  const reqUpper    = /[A-Z]/.test(newPassword);
  const reqSpecial  = /[^A-Za-z0-9]/.test(newPassword);
  const reqMatch    = !!newPassword && confirmPassword === newPassword;
  const reqDiff     = !!currentPassword && !!newPassword && currentPassword !== newPassword;

  const canSubmit = !!currentPassword && reqLength && reqLower && reqUpper && reqSpecial && reqMatch && reqDiff;

  const validate = () => {
    const errs = [];
    if (!userId) errs.push("Missing user id.");
    if (!currentPassword) errs.push("Current password is required.");
    if (!newPassword) errs.push("New password is required.");
    else {
      if (!reqLength)  errs.push("New password must be at least 6 characters.");
      if (!reqLower)   errs.push("New password must include a lowercase letter.");
      if (!reqUpper)   errs.push("New password must include an uppercase letter.");
      if (!reqSpecial) errs.push("New password must include a special character.");
    }
    if (confirmPassword !== newPassword) errs.push("Passwords do not match.");
    if (currentPassword && newPassword && currentPassword === newPassword)
      errs.push("New password must differ from current password.");
    setErrors(errs);
    return errs.length === 0;
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    setServerError("");
    if (!validate()) return;

    setIsSubmitting(true);
    const base = domain ? String(domain).replace(/\/+$/, "") : "";
    const url = `${base}/api/users/changepassword/${encodeURIComponent(userId)}`;

    fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ currentPassword, newPassword }),
      redirect: "follow",
      credentials: "include",
    })
      .then(async (response) => {
        const text = await response.text().catch(() => "");
        const data = tryParseJSON(text);
        if (!response.ok) {
          const msg = (data && (data.message || data.error)) || text || "Password update failed";
          throw new Error(msg);
        }
        return data || {};
      })
      .then(() => {
        setIsSubmitting(false);
        setErrors([]);
        setServerError("");
        setStep(2);
      })
      .catch((err) => {
        setIsSubmitting(false);
        setServerError(String(err?.message || "").trim() || "Password update failed. Please try again.");
      });
  };

  return (
    <>
      <style>{`
        .change-password-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .change-password-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .change-password-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .change-password-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="change-password-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: "rgba(255,81,0,0.08)",
              border: "1.5px solid rgba(255,81,0,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fas fa-lock" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Change Password</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                {step === 2 ? "Password updated successfully" : "Update your account password"}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {step === 2 ? (
            <div style={{ textAlign: "center", padding: "20px 0 12px" }}>
              <div style={{
                width: 72, height: 72, borderRadius: "50%", margin: "0 auto 16px",
                background: "rgba(255,81,0,0.08)", border: "2px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="fas fa-check" style={{ fontSize: 28, color: "#ff5100" }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", marginBottom: 8 }}>All set!</div>
              <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>
                Your password has been updated successfully.<br />
                You may now resume your activity.
              </div>
            </div>
          ) : (
            <>
              {/* Server error */}
              {serverError && (
                <div style={{
                  background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10,
                  padding: "10px 14px", marginBottom: 16,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <i className="fas fa-exclamation-circle" style={{ color: "#ef4444", fontSize: 13, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: "#991b1b" }}>{serverError}</span>
                </div>
              )}

              {/* Validation errors */}
              {errors.length > 0 && (
                <div style={{
                  background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10,
                  padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#991b1b",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, marginBottom: 6 }}>
                    <i className="fas fa-exclamation-triangle" style={{ fontSize: 12 }} />
                    Please fix the following:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}

              {/* Current password */}
              <div>
                <FieldLabel>Current Password</FieldLabel>
                <PasswordInput
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  show={showPasswords}
                  autoComplete="current-password"
                />
              </div>

              {/* New password */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel>New Password</FieldLabel>
                <PasswordInput
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  show={showPasswords}
                  autoComplete="new-password"
                />
              </div>

              {/* Confirm password */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel>Confirm New Password</FieldLabel>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  show={showPasswords}
                  autoComplete="new-password"
                />
              </div>

              {/* Show passwords toggle */}
              <button
                type="button"
                onClick={() => setShowPasswords((p) => !p)}
                style={{
                  marginTop: 10, background: "none", border: "none",
                  cursor: "pointer", padding: 0, fontSize: 12,
                  color: "#9ca3af", display: "flex", alignItems: "center", gap: 5,
                }}
              >
                <i className={`fas ${showPasswords ? "fa-eye-slash" : "fa-eye"}`} style={{ fontSize: 12 }} />
                {showPasswords ? "Hide passwords" : "Show passwords"}
              </button>

              {/* Requirements checklist */}
              {newPassword.length > 0 && (
                <div style={{
                  marginTop: 14, background: "#f9fafb", border: "1px solid #f3f4f6",
                  borderRadius: 10, padding: "12px 14px",
                  display: "flex", flexDirection: "column", gap: 6,
                }}>
                  <RequirementRow met={reqLength}  label="At least 6 characters" />
                  <RequirementRow met={reqUpper}   label="One uppercase letter" />
                  <RequirementRow met={reqLower}   label="One lowercase letter" />
                  <RequirementRow met={reqSpecial} label="One special character" />
                  <RequirementRow met={reqDiff}    label="Different from current password" />
                  {confirmPassword.length > 0 && (
                    <RequirementRow met={reqMatch} label="Passwords match" />
                  )}
                </div>
              )}

              <div style={{ marginTop: 14, fontSize: 12, color: "#9ca3af" }}>
                By proceeding, you accept our{" "}
                <Link to="/termsofservice" target="_blank" style={{ color: "#ff5100", fontWeight: 600 }}>
                  Terms of Service
                </Link>
                .
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {step === 2 ? (
            <button
              type="button"
              onClick={handleClose}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
                color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
              }}
            >
              Done
              <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !canSubmit}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 10, border: "none",
                background: (isSubmitting || !canSubmit) ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
                color: (isSubmitting || !canSubmit) ? "#9ca3af" : "#fff",
                fontWeight: 700, fontSize: 15,
                cursor: (isSubmitting || !canSubmit) ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 9, transition: "background 0.15s",
              }}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                  Updating…
                </>
              ) : (
                <>
                  Update Password
                  <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
                </>
              )}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChangePasswordModal;
